import { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useFieldArray, useForm, type Control, type UseFormRegister } from 'react-hook-form';
import { Anchor, Binoculars, Copy, Plus, Save, Trash2 } from 'lucide-react';
import {
  Button,
  Checkbox,
  ErrorState,
  FormActions,
  FormField,
  FormGrid,
  IconButton,
  Loading,
  PageHeader,
  Panel,
  StatusBadge,
  Tabs,
  Textarea,
  TextField,
  useConfirm,
  useToast,
} from '@/components';
import { useFiscalYear } from '@/fiscal-year/FiscalYearProvider';
import { IssueList } from '@/features/advance-notices/components/IssueList';
import { GearFindingTable } from '@/features/voyage-reports/components/GearFindingTable';
import { SightingTable, type SightingsFormPart } from '@/features/voyage-reports/components/SightingTable';
import { gearFromDto, gearsToInput, sightingFromDto, sightingsToInput, type GearRow, type SightingRow } from '@/features/voyage-reports/sightings';
import { ApiError, errorMessage } from '@/lib/api';
import { toWareki, weekdayJa } from '@/lib/wareki';
import { useFixedNewContext, useFixedReport, useSaveFixedReport, type CopyCandidate, type FixedNewContext, type FixedReportDto } from './api';
import '@/features/advance-notices/advance-notices.css';
import '@/features/voyage-reports/voyage-reports.css';

type WorkSummary = { startTime: string; endTime: string; surveyArea: string; foundGear: string; foundVessel: string; other: string };
type WorkPlan = { surveyArea: string; other: string };
type FixedForm = {
  reportTime: string;
  captainName: string;
  surveyor2Name: string;
  surveyor3Name: string;
  latDeg: string;
  latMin: string;
  latSec: string;
  lonDeg: string;
  lonMin: string;
  lonSec: string;
  zoneNo: string;
  course: string;
  speed: string;
  weather: string;
  pressure: string;
  visibility: string;
  windDirection: string;
  windForce: string;
  waves: string;
  swell: string;
  otherNotes: string;
  workSummaries: WorkSummary[];
  workPlans: WorkPlan[];
  sightings: SightingRow[];
  gearFindings: GearRow[];
};

const s = (v: string | null | undefined) => v ?? '';
const emptySummary = (): WorkSummary => ({ startTime: '', endTime: '', surveyArea: '', foundGear: '', foundVessel: '', other: '' });

/** S-31 定時連絡 登録・編集（new?noticeVesselId=&date= ／ :id/edit） */
export function FixedReportFormPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const { yearPath } = useFiscalYear();
  const reportId = id ? Number(id) : null;
  const detail = useFixedReport(reportId);
  const rowId = reportId ? (detail.data?.noticeVesselId ?? null) : Number(params.get('noticeVesselId')) || null;
  const date = reportId ? (detail.data?.reportDate ?? null) : params.get('date');
  const ctx = useFixedNewContext(rowId, date);
  if (reportId && detail.isPending) return <Loading block />;
  if (reportId && detail.error) return <ErrorState error={detail.error} onRetry={() => void detail.refetch()} />;
  if (reportId && detail.data && !detail.data.noticeVesselId) return <ErrorState error={new Error('起点の事前連絡が削除されているため編集できません。')} />;
  if (!rowId || !date) return <ErrorState error={new Error('事前連絡の実施船と発信日を指定してください（事前連絡一覧の「定時連絡登録」から開いてください）。')} />;
  if (ctx.isPending) return <Loading block />;
  if (ctx.error || !ctx.data) return <ErrorState error={ctx.error} onRetry={() => void ctx.refetch()} />;
  if (!reportId && ctx.data.existingId) return <Navigate to={yearPath(`fixed-reports/${ctx.data.existingId}/edit`)} replace />;
  return <FixedFormBody key={`${reportId ?? 'new'}-${date}`} ctx={ctx.data} detail={reportId ? detail.data : undefined} />;
}

function toForm(ctx: FixedNewContext, d?: FixedReportDto): FixedForm {
  return {
    reportTime: d ? s(d.reportTime) : ctx.prefill.reportTime,
    captainName: d ? s(d.captainName) : ctx.prefill.captainName,
    surveyor2Name: s(d?.surveyor2Name),
    surveyor3Name: s(d?.surveyor3Name),
    latDeg: s(d?.latDeg),
    latMin: s(d?.latMin),
    latSec: s(d?.latSec),
    lonDeg: s(d?.lonDeg),
    lonMin: s(d?.lonMin),
    lonSec: s(d?.lonSec),
    zoneNo: s(d?.zoneNo),
    course: s(d?.course),
    speed: s(d?.speed),
    weather: s(d?.weather),
    pressure: s(d?.pressure),
    visibility: s(d?.visibility),
    windDirection: s(d?.windDirection),
    windForce: s(d?.windForce),
    waves: s(d?.waves),
    swell: s(d?.swell),
    otherNotes: s(d?.otherNotes),
    workSummaries: d?.workSummaries.length
      ? d.workSummaries.map((w) => ({ startTime: s(w.startTime), endTime: s(w.endTime), surveyArea: s(w.surveyArea), foundGear: s(w.foundGear), foundVessel: s(w.foundVessel), other: s(w.other) }))
      : [emptySummary(), emptySummary()],
    workPlans: d?.workPlans.length ? d.workPlans.map((w) => ({ surveyArea: s(w.surveyArea), other: s(w.other) })) : [{ surveyArea: '', other: '' }],
    sightings: (d?.sightings ?? ctx.sightings).map(sightingFromDto),
    gearFindings: (d?.gearFindings ?? ctx.gearFindings).map(gearFromDto),
  };
}

function FixedFormBody({ ctx, detail }: { ctx: FixedNewContext; detail?: FixedReportDto }) {
  const isNew = !detail;
  const navigate = useNavigate();
  const toast = useToast();
  const confirm = useConfirm();
  const { yearPath } = useFiscalYear();
  const save = useSaveFixedReport(detail?.id ?? null);
  const p = ctx.prefill;
  const date = detail?.reportDate ?? p.reportDate;
  const { register, control, handleSubmit, formState } = useForm<FixedForm>({ defaultValues: toForm(ctx, detail) });
  const summaries = useFieldArray({ control, name: 'workSummaries' });
  const plans = useFieldArray({ control, name: 'workPlans' });
  const [issues, setIssues] = useState<{ message: string }[]>([]);
  const [copyTo, setCopyTo] = useState<Record<number, { checked: boolean; overwrite: boolean }>>({});
  useEffect(() => setIssues([]), [date]);
  const sgControl = control as unknown as Control<SightingsFormPart>;
  const sgRegister = register as unknown as UseFormRegister<SightingsFormPart>;
  const [tab, setTab] = useState('sightings');
  /** 「視認入力へ」「漁具入力へ」：タブを切り替えて入力欄までスクロール（kintone 検証指摘 R08.09.16） */
  const jumpTo = (key: 'sightings' | 'gears') => {
    setTab(key);
    document.getElementById('fixed-findings')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const onSubmit = handleSubmit(async (v) => {
    setIssues([]);
    const nz = (x: string) => (x.trim() === '' ? null : x);
    const input = {
      noticeVesselId: p.noticeVesselId,
      reportDate: date,
      ...Object.fromEntries(
        Object.entries(v)
          .filter(([k]) => !['workSummaries', 'workPlans', 'sightings', 'gearFindings'].includes(k))
          .map(([k, x]) => [k, nz(String(x))]),
      ),
      workSummaries: v.workSummaries.map((w) => Object.fromEntries(Object.entries(w).map(([k, x]) => [k, nz(x)]))),
      workPlans: v.workPlans.map((w) => ({ surveyArea: nz(w.surveyArea), other: nz(w.other) })),
      sightings: sightingsToInput(v.sightings),
      gearFindings: gearsToInput(v.gearFindings),
      copyTo: Object.entries(copyTo)
        .filter(([, c]) => c.checked)
        .map(([k, c]) => ({ noticeVesselId: Number(k), overwrite: c.overwrite })),
    };
    try {
      const res = await save.mutateAsync(input);
      const copied = res.copied.filter((c) => !c.skipped).length;
      const skipped = res.copied.filter((c) => c.skipped).length;
      toast.success(isNew ? '定時連絡を登録しました' : '定時連絡を更新しました', res.copied.length ? `複数選択：${copied}隻にコピー${skipped ? `、${skipped}隻は既存のため上書きしませんでした` : ''}` : undefined);
      navigate(yearPath(`fixed-reports/${res.id}`));
    } catch (e) {
      if (e instanceof ApiError && Array.isArray(e.details)) setIssues(e.details as { message: string }[]);
      else toast.error('保存できませんでした', errorMessage(e));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  });

  const onCancel = async () => {
    if (formState.isDirty && !(await confirm({ title: '入力内容を破棄しますか？', message: '保存していない変更は失われます。', confirmLabel: '破棄して戻る', tone: 'danger' }))) return;
    navigate(detail ? yearPath(`fixed-reports/${detail.id}`) : yearPath('advance-notices'));
  };
  const captainList = useMemo(() => [...new Set([...p.captainOptions, ...p.crewOptions])], [p]);

  return (
    <form onSubmit={onSubmit} noValidate>
      <PageHeader
        eyebrow="日々の報告 ／ 定時連絡"
        title={isNew ? '定時連絡 新規登録' : '定時連絡 編集'}
        meta={<StatusBadge tone="navy">{toWareki(date)}（{weekdayJa(date)}）</StatusBadge>}
        description={`${p.vesselName}（${p.businessOperatorName}）　出港 ${toWareki(p.periodFrom)} ～ 入港 ${toWareki(p.periodTo)}`}
      />
      <IssueList tone="error" title="入力内容を確認してください" items={issues} />
      <datalist id="fixed-crew-list">
        {captainList.map((n) => (
          <option key={n} value={n} />
        ))}
      </datalist>
      <Panel title="送信書">
        <FormGrid columns={4}>
          <FormField label="発信船">
            <TextField value={`${p.vesselName}（${p.registrationNo}）`} readOnly />
          </FormField>
          <FormField label="船長">
            <TextField list="fixed-crew-list" {...register('captainName')} />
          </FormField>
          <FormField label="乗船調査員名②">
            <TextField list="fixed-crew-list" {...register('surveyor2Name')} />
          </FormField>
          <FormField label="乗船調査員名③">
            <TextField list="fixed-crew-list" {...register('surveyor3Name')} />
          </FormField>
        </FormGrid>
      </Panel>
      <Panel title="正午の定時連絡">
        <div className="bs-noon">
          <FormField label="年月日">
            <TextField value={toWareki(date)} readOnly />
          </FormField>
          <FormField label="時刻" hint="HH:MM">
            <TextField className="bs-num" {...register('reportTime')} />
          </FormField>
          <FormField label="位置（緯度 N）" className="is-w2" hint="度・分・秒（全角は半角化して保存）">
            <span className="bs-dms">
              <TextField width="xs" aria-label="緯度（度）" {...register('latDeg')} />°
              <TextField width="xs" aria-label="緯度（分）" {...register('latMin')} />′
              <TextField width="xs" aria-label="緯度（秒）" {...register('latSec')} />
            </span>
          </FormField>
          <FormField label="位置（経度 E）" className="is-w2">
            <span className="bs-dms">
              <TextField width="xs" aria-label="経度（度）" {...register('lonDeg')} />°
              <TextField width="xs" aria-label="経度（分）" {...register('lonMin')} />′
              <TextField width="xs" aria-label="経度（秒）" {...register('lonSec')} />
            </span>
          </FormField>
          <FormField label="農林漁区番号">
            <TextField className="bs-num" {...register('zoneNo')} />
          </FormField>
          <FormField label="針路">
            <TextField {...register('course')} />
          </FormField>
          <FormField label="速力">
            <TextField {...register('speed')} />
          </FormField>
          <FormField label="天候">
            <TextField {...register('weather')} />
          </FormField>
          <FormField label="気圧">
            <TextField {...register('pressure')} />
          </FormField>
          <FormField label="視程">
            <TextField {...register('visibility')} />
          </FormField>
          <FormField label="風向">
            <TextField {...register('windDirection')} />
          </FormField>
          <FormField label="風力">
            <TextField {...register('windForce')} />
          </FormField>
          <FormField label="風浪">
            <TextField {...register('waves')} />
          </FormField>
          <FormField label="うねり">
            <TextField {...register('swell')} />
          </FormField>
        </div>
      </Panel>
      <Panel
        title="作業概要"
        description="送信書には先頭 2 件が出力されます（3 件目以降は「その他の記事」に追記されます）。外国漁船の視認・漁具の情報は、下の「視認状況報告・漁場調査等」に入力してください。"
        actions={
          <>
            <Button size="sm" variant="ghost" icon={<Binoculars size={15} />} onClick={() => jumpTo('sightings')}>
              視認入力へ
            </Button>
            <Button size="sm" variant="ghost" icon={<Anchor size={15} />} onClick={() => jumpTo('gears')}>
              漁具入力へ
            </Button>
            <Button size="sm" icon={<Plus size={15} />} onClick={() => summaries.append(emptySummary())}>
              行を追加
            </Button>
          </>
        }
      >
        <table className="bs-work-table">
          <thead>
            <tr>
              <th style={{ width: 236 }}>作業時間</th>
              <th>調査海域</th>
              <th>発見漁具</th>
              <th>発見船舶</th>
              <th>その他</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {summaries.fields.map((f, i) => (
              <tr key={f.id}>
                <td>
                  <span className="bs-time-range bs-time-range--example">
                    <TextField aria-label="開始時刻" placeholder="（例）06:00" {...register(`workSummaries.${i}.startTime`)} />～
                    <TextField aria-label="終了時刻" placeholder="（例）12:00" {...register(`workSummaries.${i}.endTime`)} />
                  </span>
                </td>
                <td>
                  <TextField aria-label="調査海域" {...register(`workSummaries.${i}.surveyArea`)} />
                </td>
                <td>
                  <TextField aria-label="発見漁具" {...register(`workSummaries.${i}.foundGear`)} />
                </td>
                <td>
                  <TextField aria-label="発見船舶" {...register(`workSummaries.${i}.foundVessel`)} />
                </td>
                <td>
                  <TextField aria-label="その他" {...register(`workSummaries.${i}.other`)} />
                </td>
                <td className="is-ops">
                  <IconButton label="行を削除" variant="danger" size="sm" icon={<Trash2 size={14} />} onClick={() => summaries.remove(i)} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>
      <Panel
        title="作業予定"
        actions={
          <Button size="sm" icon={<Plus size={15} />} onClick={() => plans.append({ surveyArea: '', other: '' })}>
            行を追加
          </Button>
        }
      >
        <table className="bs-work-table">
          <thead>
            <tr>
              <th>調査海域</th>
              <th>その他</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {plans.fields.map((f, i) => (
              <tr key={f.id}>
                <td>
                  <TextField aria-label="作業予定の調査海域" {...register(`workPlans.${i}.surveyArea`)} />
                </td>
                <td>
                  <TextField aria-label="作業予定のその他" {...register(`workPlans.${i}.other`)} />
                </td>
                <td className="is-ops">
                  <IconButton label="行を削除" variant="danger" size="sm" icon={<Trash2 size={14} />} onClick={() => plans.remove(i)} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <FormField label="その他の記事">
          <Textarea rows={3} {...register('otherNotes')} />
        </FormField>
      </Panel>
      <div id="fixed-findings" style={{ scrollMarginTop: 80 }} />
      <Panel title="視認状況報告・漁場調査等" description="当日の視認・漁具情報です。速報と同じデータを共有します。">
        <Tabs
          value={tab}
          onChange={setTab}
          items={[
            { key: 'sightings', label: '視認状況報告', content: <SightingTable control={sgControl} register={sgRegister} defaultDate={date} refSource="fixed" errors={formState.errors.sightings} /> },
            { key: 'gears', label: '漁場調査等（漁具）', content: <GearFindingTable control={sgControl} register={sgRegister} defaultDate={date} refSource="fixed" errors={formState.errors.gearFindings} /> },
          ]}
        />
      </Panel>
      <CopyPanel candidates={ctx.copyCandidates} value={copyTo} onChange={setCopyTo} />
      <FormActions>
        <Button onClick={() => void onCancel()}>キャンセル</Button>
        <Button type="submit" variant="primary" icon={<Save size={16} />} loading={save.isPending}>
          {isNew ? '登録する' : '保存する'}
        </Button>
      </FormActions>
    </form>
  );
}

/** 複数選択：同日出港中の自組織他船へ内容をコピーして登録（L-21-7） */
function CopyPanel({
  candidates,
  value,
  onChange,
}: {
  candidates: CopyCandidate[];
  value: Record<number, { checked: boolean; overwrite: boolean }>;
  onChange: (v: Record<number, { checked: boolean; overwrite: boolean }>) => void;
}) {
  if (!candidates.length) return null;
  const set = (id: number, patch: Partial<{ checked: boolean; overwrite: boolean }>) =>
    onChange({ ...value, [id]: { checked: false, overwrite: false, ...value[id], ...patch } });
  return (
    <Panel
      title={
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <Copy size={17} /> 複数選択（表示内容を他の船へコピー）
        </span>
      }
      description="※チェックした場合、表示されている定時連絡の内容（正午の定時連絡・作業概要・作業予定・視認・漁具）をチェックした漁船にコピーして登録することができます（同じ日に出港中の自組織の船が表示されます）。"
    >
      <ul className="bs-copy-list">
        {candidates.map((c) => {
          const v = value[c.noticeVesselId];
          return (
            <li key={c.noticeVesselId} className={v?.checked ? 'is-checked' : undefined}>
              <Checkbox label={c.vesselName ?? ''} checked={!!v?.checked} onChange={(e) => set(c.noticeVesselId, { checked: e.target.checked })} />
              <span className="bs-copy-list__sub">
                {c.registrationNo}　{c.departureDate.slice(5).replace('-', '/')}～{c.arrivalDate.slice(5).replace('-', '/')}
              </span>
              {c.existingId && (
                <span>
                  <StatusBadge tone="warning">既に登録あり</StatusBadge>{' '}
                  <Checkbox label="上書きする" checked={!!v?.overwrite} disabled={!v?.checked} onChange={(e) => set(c.noticeVesselId, { overwrite: e.target.checked })} />
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </Panel>
  );
}
